import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  Search, 
  Plus, 
  FileText, 
  Send,
  CheckCircle2,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInvoices() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    project_id: "",
    amount: "",
    due_date: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/projects`),
      ]);
      setInvoices(invoicesRes.data);
      setProjects(projectsRes.data.filter(p => p.status === "completed" || p.status === "invoiced"));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, {
        project_id: invoiceData.project_id,
        amount: parseFloat(invoiceData.amount),
        currency: "NOK",
        due_date: invoiceData.due_date,
        line_items: [{ description: "Scaffolding Services", amount: parseFloat(invoiceData.amount) }],
      });
      toast.success(t('success.created'));
      setCreateDialogOpen(false);
      setInvoiceData({ project_id: "", amount: "", due_date: "" });
      fetchData();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/send`);
      toast.success(t('success.sent'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/mark-paid`, {
        payment_reference: `MANUAL-${Date.now()}`,
      });
      toast.success(t('success.updated'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const statusColors = {
    draft: "bg-gray-500/20 text-gray-400",
    sent: "bg-blue-500/20 text-blue-400",
    paid: "bg-green-500/20 text-green-400",
    overdue: "bg-red-500/20 text-red-400",
  };

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout navItems={adminNavItems} title={t('nav.invoices')}>
      <div className="space-y-6" data-testid="admin-invoices">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 w-full sm:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
              <Input
                type="text"
                placeholder={t('common.search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
              />
            </div>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold" data-testid="create-invoice-btn">
                <Plus className="w-5 h-5 mr-2" />
                {t('invoices.createInvoice')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-['Unbounded']">{t('invoices.createInvoice')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateInvoice} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('projects.title')}</Label>
                  <select
                    value={invoiceData.project_id}
                    onChange={(e) => setInvoiceData({ ...invoiceData, project_id: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
                    required
                  >
                    <option value="">{t('admin.selectProject')}</option>
                    {projects.map((project) => (
                      <option key={project.project_id} value={project.project_id}>
                        {project.title} - {project.location_city}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.amountNok')}</Label>
                  <Input
                    type="number"
                    value={invoiceData.amount}
                    onChange={(e) => setInvoiceData({ ...invoiceData, amount: e.target.value })}
                    placeholder="50000"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.dueDate')}</Label>
                  <Input
                    type="date"
                    value={invoiceData.due_date}
                    onChange={(e) => setInvoiceData({ ...invoiceData, due_date: e.target.value })}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateDialogOpen(false)}
                    className="border-white/20 text-[#94A3B8]"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                    {t('common.create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg">
                  <FileText className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                    {invoices.length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('admin.total')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-gray-400">
                    {invoices.filter(i => i.status === "draft").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('admin.draft')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <Send className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-blue-400">
                    {invoices.filter(i => i.status === "sent").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('admin.sent')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {invoices.filter(i => i.status === "paid").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('admin.paid')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('admin.noInvoicesFound')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.invoiceNumber')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.amount')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.dueDate')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('common.status')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <p className="text-white font-medium">{invoice.invoice_number}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-[#23F7DD] font-['Unbounded'] font-bold">
                            {invoice.amount?.toLocaleString()} {invoice.currency}
                          </p>
                        </td>
                        <td className="p-4">
                          <p className="text-[#94A3B8]">{invoice.due_date || "N/A"}</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[invoice.status] || "bg-gray-500/20 text-gray-400"}`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {invoice.status === "draft" && (
                              <Button
                                size="sm"
                                onClick={() => handleSendInvoice(invoice.invoice_id)}
                                className="bg-blue-500 text-white hover:bg-blue-600"
                                data-testid={`send-invoice-${invoice.invoice_id}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                {t('admin.send')}
                              </Button>
                            )}
                            {invoice.status === "sent" && (
                              <Button
                                size="sm"
                                onClick={() => handleMarkPaid(invoice.invoice_id)}
                                className="bg-green-500 text-white hover:bg-green-600"
                                data-testid={`mark-paid-${invoice.invoice_id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                {t('admin.markPaid')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
